import React, { useState, useEffect, useRef } from "react";
import AIExpansionModal from "./components/ai/AIExpansionModal";
import { useIsSubscribed } from "./hooks/useIsSubscribed";
import { useTier } from "./hooks/useTier";
import Paywall from "./components/Paywall";
import { trackContentView, trackFeatureUsed } from "./services/analyticsService";
import JsonContentRenderer from "./components/JsonContentRenderer";
import { useTranslation } from "./hooks/useTranslation";
import { useLanguage } from "./components/language/LanguageContext";
import { translateTreeLabel } from "./utils/translateTreeLabel";

export default ({ isOpen, onClose, selectedNode }) => {
  const t = useTranslation();
  const { language } = useLanguage();
  const [pageContent, setPageContent] = useState(null);
  const [jsonContent, setJsonContent] = useState(null); // NEW: JSON content state
  const [isJsonMode, setIsJsonMode] = useState(false); // NEW: Track if rendering JSON
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [audioExists, setAudioExists] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [selectedSectionContent, setSelectedSectionContent] = useState('');
  const [selectedSectionTitle, setSelectedSectionTitle] = useState('');
  const [fullContent, setFullContent] = useState('');
  const { isSubscribed, loading: subscriptionLoading } = useIsSubscribed();
  const { hasContentAccess, loading: tierLoading } = useTier();
  const audioRef = useRef(null);

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  // Check if audio file exists using Image preload technique
  // On iOS, we show the player optimistically since iOS won't load metadata without user interaction
  useEffect(() => {
    if (selectedNode?.page) {
      const audioPath = `audio/${selectedNode.page}.mp3`;
      console.log('audioPath', audioPath);
      
      // On iOS, show audio player optimistically (iOS won't load metadata without user interaction)
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        // Optimistically assume audio exists on iOS - the audio element will handle errors gracefully
        setAudioExists(true);
        return;
      }
      
      // For non-iOS devices, check if file exists
      const testAudio = new Audio();
      testAudio.preload = 'metadata';
      
      let timeout;
      
      const handleCanPlay = () => {
        clearTimeout(timeout);
        setAudioExists(true);
      };
      
      const handleError = () => {
        clearTimeout(timeout);
        setAudioExists(false);
      };
      
      testAudio.addEventListener('canplaythrough', handleCanPlay);
      testAudio.addEventListener('error', handleError);
      
      testAudio.src = audioPath;
      
      // Set a longer timeout for slower connections (5 seconds)
      timeout = setTimeout(() => {
        // If timeout, assume it doesn't exist (but this might be a network issue)
        setAudioExists(false);
      }, 5000);
      
      return () => {
        clearTimeout(timeout);
        testAudio.removeEventListener('canplaythrough', handleCanPlay);
        testAudio.removeEventListener('error', handleError);
        testAudio.src = '';
      };
    } else {
      setAudioExists(false);
    }
  }, [selectedNode]);

  // Handle download requests from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'DOWNLOAD_FILE') {
        console.log('Download request received from iframe:', event.data);
        
        const { url, filename } = event.data;
        
        // Create download link in parent window
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('Download triggered from parent window');
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Function to inject buttons into section cards
  const injectButtonsIntoSectionCards = () => {
    try {
      const iframe = document.querySelector('.content-iframe');
      if (!iframe || !iframe.contentDocument) return;

      // const sectionCards = iframe.contentDocument.querySelectorAll('.section-card');
      const sectionCards = iframe.contentDocument.querySelectorAll('section, .section, .content-section, .credit-type-card');
      
      sectionCards.forEach((card, index) => {
        // Check if button already exists to avoid duplicates
        if (card.querySelector('.injected-button')) return;
        
        // Create button
        const button = iframe.contentDocument.createElement('button');
        button.className = 'injected-button';
        button.innerHTML = '🤖';
        button.title = t('sidebar.quickAction', 'Quick Action');
        button.style.cssText = `
          position: absolute;
          top: 10px;
          right: 10px;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid darkblue;
          background: linear-gradient(135deg, #3498db, #2980b9);
          color: white;
          font-size: 14px;
          cursor: pointer;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
        `;
        
        // Add hover effects
        button.addEventListener('mouseenter', () => {
          button.style.transform = 'scale(1.1)';
          button.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.4)';
        });
        
        button.addEventListener('mouseleave', () => {
          button.style.transform = 'scale(1)';
          button.style.boxShadow = '0 2px 8px rgba(52, 152, 219, 0.3)';
        });
        
        // Add click handler
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Get the section content
          const sectionText = card.textContent || card.innerText || '';
          const sectionTitle = card.querySelector('h1, h2, h3, h4, h5, h6')?.textContent || t('sidebar.section', 'Section');
          
          // Get the full iframe content
          const iframe = document.querySelector('.content-iframe');
          const fullContent = iframe?.contentDocument?.body?.textContent || '';
          
          // Track AI button click
          const section = window.location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                         window.location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
          
          trackFeatureUsed({
            section: section,
            feature: 'ai_expand_button',
            action: 'click',
            source: 'content_section',
            additionalParams: {
              section_title: sectionTitle,
              page_name: selectedNode?.page || 'unknown'
            }
          });
          
          // Set the content for AI expansion
          setSelectedSectionContent(sectionText);
          setSelectedSectionTitle(sectionTitle);
          setFullContent(fullContent);
          setAiModalOpen(true);
          
          console.log(`AI expansion requested for section: ${sectionTitle}`);
        });
        
        // Make sure the card has relative positioning
        if (getComputedStyle(card).position === 'static') {
          card.style.position = 'relative';
        }
        
        // Insert button at the beginning of the card
        card.insertBefore(button, card.firstChild);
      });
    } catch (error) {
      console.log('Error injecting buttons:', error);
    }
  };

  // iOS workaround: Force audio element to load when sidebar is open
  useEffect(() => {
    if (audioRef.current && isOpen && selectedNode?.page) {
      // Force iOS to recognize the audio element
      const audio = audioRef.current;
      // Load the audio source (iOS needs this)
      audio.load();
      
      // iOS Safari sometimes needs a small delay before controls appear
      const timer = setTimeout(() => {
        // Touch the audio element to activate it on iOS
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
          // Ensure controls are visible by forcing a reflow
          audio.style.display = 'none';
          audio.offsetHeight; // Trigger reflow
          audio.style.display = '';
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, selectedNode?.page]);

  const renderAudioPlayer = () => {
    // Only show audio player if we have a selectedNode.page AND audio exists
    if (!selectedNode?.page || !audioExists) return null;

    const audioPath = `audio/${selectedNode.page}.mp3`;

    const handleAudioPlay = () => {
      const section = window.location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                     window.location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
      
      trackFeatureUsed({
        section: section,
        feature: 'audio_playback',
        action: 'play',
        source: 'sidebar',
        additionalParams: {
          audio_file: selectedNode.page,
          node_label: selectedNode.label
        }
      });
    };

    return (
      <div className="audio-player-container">
        <div className="audio-player">
          <div className="audio-player-header">
            <span className="audio-icon">🎵</span>
            <span className="audio-title">{t('sidebar.audioGuide', 'Audio Guide')}</span>
          </div>
          <audio
            ref={audioRef}
            controls
            playsInline
            className="audio-element"
            preload="metadata"
            onPlay={handleAudioPlay}
            style={{
              width: '100%',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef'
            }}
          >
            <source src={audioPath} type="audio/mpeg" />
            {t('sidebar.browserNoAudioSupport', 'Your browser does not support the audio element.')}
          </audio>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isOpen && selectedNode?.page) {
      // Track content view when sidebar opens
      const section = window.location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                     window.location.pathname.startsWith('/business-credit') ? 'business_credit' :
                     window.location.pathname.startsWith('/client-mentor-sops') ? 'client_mentor_sops' : 'dashboard';
      
      trackContentView({
        section: section,
        slug: selectedNode.page,
        content_type: 'article',
        content_id: selectedNode.id,
        additionalParams: {
          node_label: selectedNode.label,
          has_premium: selectedNode.premium === 'true' || selectedNode.premium === true
        }
      });
      
      setLoading(true);
      setIsJsonMode(false);
      setJsonContent(null);
      
      // Extract the page slug from the path (e.g., 'static_html_content-personal/credit-fundamentals' -> 'credit-fundamentals')
      let pagePath = selectedNode.page.replace('.html', '');
      
      // Adjust path based on language: use _es folder for Spanish
      if (language === 'es') {
        if (pagePath.includes('static_html_content-personal')) {
          pagePath = pagePath.replace('static_html_content-personal', 'static_html_content-personal_es');
        } else if (pagePath.includes('static_html_content-business')) {
          pagePath = pagePath.replace('static_html_content-business', 'static_html_content-business_es');
        } else if (pagePath.includes('static_html_content_client_mentor')) {
          pagePath = pagePath.replace('static_html_content_client_mentor', 'static_html_content_client_mentor_es');
        }
      }
      
      // First, try to load JSON version
      const jsonPath = `/data/${pagePath}.json`;
      console.log('Checking for JSON content at:', jsonPath);
      
      fetch(jsonPath)
        .then(response => {
          if (!response.ok) {
            throw new Error('JSON not found');
          }
          return response.json();
        })
        .then(jsonData => {
          console.log('JSON content found, using JSON renderer');
          setJsonContent(jsonData);
          setIsJsonMode(true);
          setPageContent(null);
          setLoading(false);
        })
        .catch(jsonError => {
          // JSON not found, fallback to HTML
          console.log('No JSON found, falling back to HTML:', jsonError.message);
          
          // Use the language-adjusted path for HTML as well
          let page = pagePath;
          if(!page.includes('.html')) {
            page += '.html';
          }
          
          fetch(`${page}`)
            .then(response => {
              return response.text();
            })
            .then(html => {
              // Check if the content is the default React app template (indicates non-existing page)
              if (!html || 
                  html.includes('You need to enable JavaScript to run this app') ||
                  html.includes('This HTML file is a template') ||
                  html.trim().length < 100) {
                setPageContent('coming_soon');
              } else {
                setPageContent(html);
              }
              setIsJsonMode(false);
              setLoading(false);
            })
            .catch(error => {
              console.error('Error loading page:', error);
              setPageContent('coming_soon');
              setIsJsonMode(false);
              setLoading(false);
            });
        });
    } else {
      setPageContent(null);
      setJsonContent(null);
      setIsJsonMode(false);
    }
  }, [isOpen, selectedNode, language]);

  // Inject buttons when iframe loads
  useEffect(() => {
    if (pageContent && !loading) {
      // Wait a bit for the iframe to render
      const timer = setTimeout(() => {
        injectButtonsIntoSectionCards();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [pageContent, loading]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading">
          <div className="spinner"></div>
          <p>{t('sidebar.loadingContent', 'Loading content...')}</p>
        </div>
      );
    }

    if (pageContent === 'coming_soon') {
      return (
        <div className="coming-soon">
          <h4>{t('sidebar.comingSoon', 'Coming Soon...')}</h4>
          <p>{t('sidebar.contentUnderDevelopment', 'This content is currently under development.')}</p>
        </div>
      );
    }

    if (pageContent === 'error') {
      return (
        <div className="error">
          <h4>{t('sidebar.error', 'Error')}</h4>
          <p>{t('sidebar.failedToLoad', 'Failed to load content. Please try again later.')}</p>
        </div>
      );
    }

    // NEW: Render JSON content using JsonContentRenderer
    if (isJsonMode && jsonContent) {
      return (
        <div className="json-content-wrapper" style={{ height: '100%', overflow: 'auto' }}>
          <JsonContentRenderer jsonData={jsonContent} />
        </div>
      );
    }

    if (pageContent) {
      return (
        <iframe
          srcDoc={pageContent}
          title="Node Content"
          className="content-iframe"
          sandbox="allow-scripts allow-same-origin"
          onLoad={() => {
            // Inject buttons when iframe loads
            setTimeout(injectButtonsIntoSectionCards, 100);
          }}
        />
      );
    }
  };

  // Determine tree type based on current route
  const getTreeType = () => {
    if (window.location.pathname.startsWith('/personal-credit')) {
      return 'personal';
    } else if (window.location.pathname.startsWith('/business-credit')) {
      return 'business';
    } else if (window.location.pathname.startsWith('/client-mentor-sops')) {
      return 'client-mentor';
    }
    return 'business'; // default
  };

  // Translate the node label for the header
  const getTranslatedHeaderLabel = () => {
    if (!selectedNode?.label) {
      return t('sidebar.nodeDetails', 'Node Details');
    }
    const treeType = getTreeType();
    return translateTreeLabel(selectedNode.id, selectedNode.label, t, treeType);
  };
  
  return (
    <>
      <aside className={`sidebar ${isOpen ? 'open' : 'closed'}${isFullscreen ? ' fullscreen' : ''}`}>
        <div className="sidebar-header">
          <h3>{getTranslatedHeaderLabel()}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            {isJsonMode && (
              <button
                className="ai-expand-button-header"
                aria-label={t('sidebar.expandWithAI', 'Expand with AI')}
                onClick={() => {
                  const section = window.location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                                 window.location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
                  
                  // Extract text content from JSON for sectionContent
                  const extractTextFromJson = (jsonData) => {
                    if (!jsonData) return '';
                    if (typeof jsonData === 'string') return jsonData;
                    if (Array.isArray(jsonData)) {
                      return jsonData.map(item => extractTextFromJson(item)).join(' ');
                    }
                    if (typeof jsonData === 'object') {
                      return Object.values(jsonData)
                        .map(value => extractTextFromJson(value))
                        .join(' ');
                    }
                    return String(jsonData);
                  };
                  
                  const sectionContent = extractTextFromJson(jsonContent);
                  const fullContent = JSON.stringify(jsonContent, null, 2);
                  const sectionTitle = selectedNode?.label || t('sidebar.pageContent', 'Page Content');
                  
                  trackFeatureUsed({
                    section: section,
                    feature: 'ai_expand_button',
                    action: 'click',
                    source: 'json_page_header',
                    additionalParams: {
                      section_title: sectionTitle,
                      page_name: selectedNode?.page || 'unknown'
                    }
                  });
                  
                  setSelectedSectionContent(sectionContent);
                  setSelectedSectionTitle(sectionTitle);
                  setFullContent(fullContent);
                  setAiModalOpen(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: 16,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                }}
                title={t('sidebar.expandWithAI', 'Expand with AI')}
              >
                🤖
              </button>
            )}
            <button
              className="fullscreen-toggle-button"
              aria-label={isFullscreen ? t('sidebar.exitFullscreen', 'Exit Fullscreen') : t('sidebar.enterFullscreen', 'Enter Fullscreen')}
              onClick={() => {
                const section = window.location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                               window.location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
                
                trackFeatureUsed({
                  section: section,
                  feature: 'fullscreen_toggle',
                  action: isFullscreen ? 'exit' : 'enter',
                  source: 'sidebar'
                });
                
                setIsFullscreen(f => !f);
              }}
              style={{
                background: '#e3f0fc',
                color: '#2980b9',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: 18,
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 8,
                transition: 'background 0.2s',
              }}
              title={isFullscreen ? t('sidebar.exitFullscreen', 'Exit Fullscreen') : t('sidebar.enterFullscreen', 'Enter Fullscreen')}
            >
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="4" width="3" height="2" />
                  <rect x="4" y="4" width="2" height="3" />
                  <rect x="9" y="4" width="3" height="2" />
                  <rect x="10" y="4" width="2" height="3" />
                  <rect x="4" y="10" width="3" height="2" />
                  <rect x="4" y="9" width="2" height="3" />
                  <rect x="9" y="10" width="3" height="2" />
                  <rect x="10" y="9" width="2" height="3" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1V6H2V2H6V1H1Z"/>
                  <path d="M15 15V10H14V14H10V15H15Z"/>
                  <path d="M14 2V6H15V1H10V2H14Z"/>
                  <path d="M2 14V10H1V15H6V14H2Z"/>
                </svg>
              )}
            </button>
            <button 
              className="close-button-sidebar" 
              onClick={() => {
                const section = window.location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                               window.location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
                
                trackFeatureUsed({
                  section: section,
                  feature: 'sidebar',
                  action: 'close',
                  source: 'button'
                });
                
                onClose();
              }} 
              aria-label={t('sidebar.closeSidebar', 'Close Sidebar')}
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Paywall - Show only if content is premium AND user's tier doesn't have access */}
        {(() => {
          // Check if the current content is premium
          const isPremiumContent = selectedNode?.premium === 'true' || selectedNode?.premium === true;
          
          // Determine content type from path or selectedNode.page
          const path = window.location.pathname;
          const pagePath = selectedNode?.page || '';
          let contentType = 'personal_credit'; // default
          
          if (path.startsWith('/business-credit') || pagePath.includes('static_html_content-business')) {
            contentType = 'business_credit';
          } else if (path.startsWith('/personal-credit') || pagePath.includes('static_html_content-personal')) {
            contentType = 'personal_credit';
          } else if (path.startsWith('/client-mentor-sops') || pagePath.includes('static_html_content_client_mentor')) {
            contentType = 'client_mentor_sops';
          }
          
          // Show loading state while checking subscription/tier
          if (subscriptionLoading || tierLoading) {
            return (
              <div className="sidebar-content" style={{ padding: '40px', textAlign: 'center' }}>
                <div className="spinner"></div>
                <p>{t('sidebar.checkingSubscription', 'Checking subscription status...')}</p>
              </div>
            );
          }
          
          // If content is premium and user's tier doesn't have access, show paywall
          const hasAccess = !isPremiumContent || hasContentAccess(contentType);
          
          if (!hasAccess) {
            const contentLabel = selectedNode?.label || t('sidebar.pageContent', 'this content');
            const message = t('sidebar.premiumContentMessage', `This content is only available to subscribers. Subscribe now to access "${contentLabel}" and unlock all premium features.`).replace('{content}', contentLabel);
            return (
              <div className="sidebar-content sidebar-paywall-container">
                <Paywall 
                  title={t('sidebar.premiumContentLocked', 'Premium Content Locked')}
                  message={message}
                  onClose={onClose}
                />
              </div>
            );
          }
          
          // User has access, show content normally
          return (
            <>
              {renderAudioPlayer()}
              <div className={`sidebar-content ${pageContent ? 'has-iframe' : ''}`}>
                {renderContent()}
              </div>
            </>
          );
        })()}
      </aside>
      
      <AIExpansionModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        onSidebarClose={onClose}
        sectionContent={selectedSectionContent}
        fullContent={fullContent}
        sectionTitle={selectedSectionTitle}
        pageName={selectedNode?.page || 'home'}
      />
    </>
  );
};
